import React from 'react';
import {
  BORDER, TAN, DocTitle, SectionBar, Panel, Field, SelectField, CheckBox, InitialsField,
  US_STATES, REQ, V, optional, getError,
} from './ui';

const LEADERS = [
  { key: 'ceo', title: 'CEO or President:' },
  { key: 'cfo', title: 'CFO:' },
  { key: 'coo', title: 'COO:' },
  { key: 'hr', title: 'HR:' },
  { key: 'other', title: 'Other:' },
];

const PAY_FREQ = ['Weekly', 'Bi-Weekly', 'Semi-Monthly', 'Monthly'].map((v) => [v, v]);
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((v) => [v, v]);

/** Page 3 of the Word document – certification, company info, leadership team and states */
const StepPayrollInfo = ({ register, errors, watch, setValue }) => {
  const payType = watch('payrollInfo.payType');
  const primary = watch('payrollInfo.primaryContact');
  const hasW2 = payType === 'W2' || payType === 'Both';
  const has1099 = payType === '1099' || payType === 'Both';
  const f = { register, errors };

  const toggleType = (kind) => {
    const w2 = kind === 'W2' ? !hasW2 : hasW2;
    const c = kind === '1099' ? !has1099 : has1099;
    setValue('payrollInfo.payType', w2 && c ? 'Both' : w2 ? 'W2' : c ? '1099' : '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const typeError = getError(errors, 'payrollInfo.payType');
  const primaryError = getError(errors, 'payrollInfo.primaryContact');
  const statesError = getError(errors, 'payrollInfo.states');

  return (
    <div className="space-y-8">
      <DocTitle>Company Information for Onboarding</DocTitle>

      {/* ---------- certification ---------- */}
      <div className={`border ${BORDER}`}>
        <div className={`grid grid-cols-[84px_1fr] border-b ${BORDER} ${TAN} text-xs font-bold tracking-[0.2em] text-gray-700`}>
          <div className={`border-r ${BORDER} px-2 py-2 text-center`}>INITIAL</div>
          <div className="px-4 py-2 text-center">TERMS</div>
        </div>
        <div className="grid grid-cols-[84px_1fr]">
          <div className={`flex justify-center border-r ${BORDER} bg-[#faf9f7] px-1 py-4`}>
            <InitialsField name="agreement.certifyInitials" {...f} />
          </div>
          <p className="p-4 text-[13px] leading-relaxed text-gray-700 md:p-5">
            You certify, to the best of your knowledge, the accuracy of the information provided above, and any subsequent
            information provided by any other means.
          </p>
        </div>
      </div>

      {/* ---------- company information ---------- */}
      <div>
        <SectionBar>Company Information for Onboarding</SectionBar>
        <div className={`border border-t-0 ${BORDER}`}>
          {/* worker types */}
          <div className={`flex flex-wrap items-center gap-x-8 gap-y-2 border-b ${BORDER} bg-[#faf9f7] px-4 py-3`}>
            <span className="text-[13px] font-semibold text-gray-800">
              We plan to pay the following types of workers: <span className="text-red-600">*</span>
            </span>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
              <CheckBox checked={hasW2} onChange={() => toggleType('W2')} /> W-2 Employees
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
              <CheckBox checked={has1099} onChange={() => toggleType('1099')} /> 1099 Contractors
            </label>
            <input type="hidden" {...register('payrollInfo.payType', { required: 'Select at least one worker type' })} />
            {typeError && <span role="alert" className="w-full text-xs text-red-600">{typeError.message}</span>}
          </div>

          <Field {...f} layout="row" name="payrollInfo.w2EmployeeCount" label="Number of W2 Employees Paid Last Payroll:" mask="int" readOnly={!hasW2} placeholder={hasW2 ? '0' : 'N/A'} rules={{ required: hasW2 ? REQ : false }} />
          <Field {...f} layout="row" name="payrollInfo.contractorCount" label="Number of Contractors Paid Last Month:" mask="int" readOnly={!has1099} placeholder={has1099 ? '0' : 'N/A'} rules={{ required: has1099 ? REQ : false }} />
          <SelectField {...f} layout="row" name="payrollInfo.payFrequency" label="Pay Period:" options={PAY_FREQ} rules={{ required: REQ }} />
          <SelectField {...f} layout="row" name="payrollInfo.payPeriodStartingDay" label="Pay Period Starting Day of the Week:" options={DAYS} rules={{ required: REQ }} />
          <Field {...f} layout="row" name="payrollInfo.estimatedAnnualRevenue" label="Estimated Annual Revenue:" mask="money" placeholder="$0" rules={{ required: REQ }} />
          <Field {...f} layout="row" name="companyInfo.natureOfBusiness" label="Nature of Business/Services Provided" rules={{ required: REQ }} />
          <Field {...f} layout="row" name="companyInfo.naicsCode" label="NAICS Code (www.census.gov/naics/)" mask="int" placeholder="6 digits" />
          <Field {...f} layout="row" name="companyInfo.website" label="Company Website URL:" placeholder="https://" />
        </div>
      </div>

      {/* ---------- leadership team ---------- */}
      <div>
        <SectionBar>Company Leadership Team</SectionBar>
        <div className={`border border-t-0 ${BORDER}`}>
          <div className={`hidden grid-cols-[150px_90px_1.2fr_1.4fr_1fr] border-b ${BORDER} ${TAN} text-xs font-bold text-gray-700 md:grid`}>
            {['Title', 'Primary Contact', 'Full Name', 'Email', 'Phone'].map((h, i) => (
              <div key={h} className={`px-3 py-2 ${i === 1 ? 'text-center' : ''} ${i < 4 ? `border-r ${BORDER}` : ''}`}>{h}</div>
            ))}
          </div>

          {LEADERS.map(({ key, title }) => {
            const isPrimary = primary === key;
            const base = `payrollInfo.leadership.${key}`;
            const req = isPrimary ? REQ : false;
            return (
              <div key={key} className={`grid grid-cols-1 gap-2 border-b ${BORDER} px-3 py-3 last:border-b-0 md:grid-cols-[150px_90px_1.2fr_1.4fr_1fr] md:items-start md:gap-0 md:p-0 ${isPrimary ? 'bg-blue-50/50' : ''}`}>
                <div className="flex items-center justify-between md:block md:px-3 md:py-3">
                  <span className="text-[13px] font-semibold text-gray-800">{title}</span>
                  <label className="flex items-center gap-2 text-xs text-gray-600 md:hidden">
                    <CheckBox checked={isPrimary} onChange={() => setValue('payrollInfo.primaryContact', isPrimary ? '' : key, { shouldValidate: true })} />
                    Primary contact
                  </label>
                </div>
                <div className="hidden justify-center md:flex md:py-3">
                  <CheckBox
                    aria-label={`${title} is the primary contact`}
                    checked={isPrimary}
                    onChange={() => setValue('payrollInfo.primaryContact', isPrimary ? '' : key, { shouldValidate: true })}
                  />
                </div>
                <div className="md:px-2 md:py-1.5">
                  {key === 'other' && (
                    <Field {...f} layout="bare" name={`${base}.title`} label="Other title" placeholder="Title (e.g. Controller)" className="mb-1" />
                  )}
                  <Field {...f} layout="bare" name={`${base}.fullName`} label={`${title} full name`} placeholder="Full name" rules={{ required: req }} />
                </div>
                <div className="md:px-2 md:py-1.5">
                  <Field {...f} layout="bare" type="email" name={`${base}.email`} label={`${title} email`} placeholder="Email" rules={{ required: req, validate: isPrimary ? V.email : optional(V.email) }} />
                </div>
                <div className="md:px-2 md:py-1.5">
                  <Field {...f} layout="bare" name={`${base}.phone`} label={`${title} phone`} mask="phone" placeholder="Phone" rules={{ required: req, validate: isPrimary ? V.phone : optional(V.phone) }} />
                </div>
              </div>
            );
          })}
          <input type="hidden" {...register('payrollInfo.primaryContact', { required: 'Select one Primary Contact' })} />
        </div>
        {primaryError && <p role="alert" className="mt-2 text-xs text-red-600">{primaryError.message}</p>}
      </div>

      {/* ---------- states ---------- */}
      <div>
        <SectionBar>States</SectionBar>
        <div className={`border border-t-0 ${BORDER} p-4 md:p-5`}>
          <p className="mb-4 text-[13px] font-semibold text-gray-800">
            Please identify the state(s) in which you pay workers: <span className="text-red-600">*</span>
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 md:grid-flow-col md:grid-cols-5 md:grid-rows-10">
            {US_STATES.map(([abbr, name]) => (
              <label key={abbr} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-[13px] text-gray-800 hover:bg-gray-50">
                <CheckBox value={abbr} {...register('payrollInfo.states', { required: 'Select at least one state' })} />
                {name}
              </label>
            ))}
          </div>
          {statesError && <p role="alert" className="mt-3 text-xs text-red-600">{statesError.message || REQ}</p>}
        </div>
      </div>
    </div>
  );
};

export default StepPayrollInfo;
